/**
 * JCI — Dynamic Product Tabs (PDP)
 *
 * Reads the product description from a <template data-pdp-tabs-source>,
 * treats every top-level heading (H2–H6) as a tab, and builds:
 *   - a desktop tab nav + panel grid
 *   - a mobile accordion
 *
 * Each tab icon is chosen from the heading text by a prioritized keyword
 * map. The rest of the heading becomes the tab label.
 *
 * Merchants edit the product description normally in Shopify admin — no
 * per-product templates or theme edits needed. Any mix of H3/H4/etc. at
 * the top level all become their own tabs.
 */
(function () {
  'use strict';

  // Strip the leading emoji (or symbol) from a heading and return the
  // remaining text. The emoji itself is discarded \u2014 tabs render an SVG icon
  // chosen by keyword, not the merchant's emoji.
  function stripLeadingEmoji(text) {
    text = (text || '').trim();
    var re;
    try {
      re = new RegExp('^(\\p{Extended_Pictographic}(?:\\uFE0F|\\u200D\\p{Extended_Pictographic})*)\\s*', 'u');
    } catch (e) {
      re = /^([\u2600-\u27BF\uE000-\uF8FF\u{1F300}-\u{1FAFF}](?:\uFE0F)?)\s*/u;
    }
    var m = text.match(re);
    return m ? text.slice(m[0].length).trim() : text;
  }

  // Heading-text -> icon name. First matching keyword wins, so this list is
  // ordered by the Product Tabs (PDP) priority table. Headings that don't
  // match any keyword fall back to a generic info icon.
  var ICON_KEYWORD_MAP = [
    { keywords: ['video', 'videos'], icon: 'play' },
    {
      keywords: ['warranty', 'support', 'protection', 'coverage', 'compliant', 'compliance', 'dot', 'sae', 'road legal', 'legal', 'regulations', 'fmvss108'],
      icon: 'shield'
    },
    {
      keywords: ['included', 'package', 'bundle', 'kit contents', 'contents', 'accessories'],
      icon: 'box'
    },
    {
      keywords: ['installation', 'install', 'setup', 'wiring', 'wire', 'guide', 'instructions', 'notes', 'plug-and-play', 'plug and play', 'harness', 'factory harness', 'bolt-on', 'bolt on', 'mounting', 'resistor', 'adapter'],
      icon: 'wrench'
    },
    {
      keywords: ['compatibility', 'vehicle', 'fitment', 'fits', 'models', 'trim', 'trims', 'application', 'application-specific', 'vehicle-specific', 'oe fit', 'oem', 'factory'],
      icon: 'car'
    },
    { keywords: ['love', 'upgrade', 'head-turning', 'mod', 'stand out', 'benefits'], icon: 'star' },
    {
      keywords: ['rgb', 'rgbw', 'flow', 'led', 'leds', 'lighting', 'color', 'colors', 'multicolor', 'effects', 'control', 'features', 'modes', 'projector', 'output', 'beam', 'sequential', 'turn signal', 'activation', 'signature', 'styling', 'drl', 'drls', 'daytime'],
      icon: 'palette'
    },
    {
      keywords: ['specs', 'specifications', 'technical', 'line drawings', 'drawings', 'material', 'design', 'build', 'built', 'engineered', 'details', 'perform', 'performance', 'construction', 'lifespan', 'waterproof', 'rating', 'series', 'pro-series', 'luxx', 'nova', 'xb', 'evo', 'hybrid'],
      icon: 'gear'
    },
    { keywords: ['overview', 'description', 'what are', 'product overview', 'info'], icon: 'info' }
  ];

  var SVG_ATTR = 'xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';

  var ICON_SVG = {
    star:    '<svg ' + SVG_ATTR + '><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>',
    palette: '<svg ' + SVG_ATTR + '><circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/><circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/><path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 10 10 5 5 0 0 1-5 5h-1.76a2 2 0 0 0-1.41 3.41 2 2 0 0 1-1.41 3.41Z"/></svg>',
    gear:    '<svg ' + SVG_ATTR + '><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>',
    car:     '<svg ' + SVG_ATTR + '><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/></svg>',
    wrench:  '<svg ' + SVG_ATTR + '><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>',
    box:     '<svg ' + SVG_ATTR + '><path d="m7.5 4.27 9 5.15"/><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/></svg>',
    shield:  '<svg ' + SVG_ATTR + '><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
    play:    '<svg ' + SVG_ATTR + '><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>',
    info:    '<svg ' + SVG_ATTR + '><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>'
  };

  function escapeRegExp(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function keywordMatches(text, keyword) {
    var pattern = keyword.split(/[ -]+/).map(escapeRegExp).join('[\\s-]+');
    return new RegExp('(^|[^a-z0-9])' + pattern + '(?=[^a-z0-9]|$)').test(text);
  }

  function iconForHeading(title) {
    var t = (title || '').toLowerCase();
    for (var i = 0; i < ICON_KEYWORD_MAP.length; i++) {
      var entry = ICON_KEYWORD_MAP[i];
      for (var j = 0; j < entry.keywords.length; j++) {
        if (keywordMatches(t, entry.keywords[j])) {
          return ICON_SVG[entry.icon];
        }
      }
    }
    return ICON_SVG.info;
  }

  function slugify(str) {
    return (str || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'tab';
  }

  // Split the source HTML into [{ id, emoji, title, contentHTML }, ...],
  // one entry per heading. Headings (H2-H6) are found via querySelectorAll
  // so they're detected at any nesting depth — Shopify's RTE and merchant
  // copy-paste sometimes wrap headings in extra <div>s or leave stray
  // siblings outside the main wrapper. A DOM Range extracts the content
  // between consecutive headings, preserving wrapping ancestors.
  function parseSections(sourceHTML) {
    var parser = new DOMParser();
    var doc = parser.parseFromString('<div id="_root">' + sourceHTML + '</div>', 'text/html');
    var root = doc.getElementById('_root');
    if (!root) return [];

    // Filter out "headings" with no text — merchants sometimes wrap a bare
    // iframe or image in <h2>, which would otherwise create a phantom tab.
    var headings = Array.prototype.filter.call(
      root.querySelectorAll('h2, h3, h4, h5, h6'),
      function (h) { return (h.textContent || '').trim().length > 0; }
    );
    if (!headings.length) return [];

    var sections = [];
    var usedIds = {};

    for (var i = 0; i < headings.length; i++) {
      var h = headings[i];
      var nextH = headings[i + 1];
      var title = stripLeadingEmoji(h.textContent || '');
      var baseId = slugify(title);
      var id = baseId;
      var n = 2;
      while (usedIds[id]) { id = baseId + '-' + n; n++; }
      usedIds[id] = true;

      var range = doc.createRange();
      range.selectNodeContents(root);
      range.setStartAfter(h);
      if (nextH) range.setEndBefore(nextH);
      var temp = doc.createElement('div');
      temp.appendChild(range.cloneContents());

      sections.push({
        id: 'pdp-tab-' + id,
        title: title,
        contentHTML: temp.innerHTML
      });
    }
    return sections;
  }

  function buildDesktopNav(nav, sections) {
    nav.innerHTML = '';
    sections.forEach(function (s, idx) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp-tabs__tab-btn' + (idx === 0 ? ' is-active' : '');
      btn.setAttribute('data-tab-target', s.id);
      btn.innerHTML =
        '<span class="pdp-tabs__tab-icon">' + iconForHeading(s.title) + '</span>' +
        '<span class="pdp-tabs__tab-text">' + escapeHTML(s.title) + '</span>';
      nav.appendChild(btn);
    });
  }

  function buildDesktopPanels(content, sections) {
    content.innerHTML = '';
    sections.forEach(function (s, idx) {
      var panel = document.createElement('div');
      panel.className = 'pdp-tabs__panel' + (idx === 0 ? ' is-active' : '');
      panel.id = s.id;
      panel.innerHTML =
        '<div class="pdp-tabs__panel-content">' +
          '<h2>' + escapeHTML(s.title) + '</h2>' +
          s.contentHTML +
        '</div>';
      content.appendChild(panel);
    });
  }

  function buildMobileAccordion(accordion, sections) {
    accordion.innerHTML = '';
    sections.forEach(function (s, idx) {
      var accId = 'pdp-acc-' + s.id.replace(/^pdp-tab-/, '');
      var item = document.createElement('div');
      item.className = 'pdp-tabs__accordion-item';
      item.innerHTML =
        '<button class="pdp-tabs__accordion-trigger" type="button" data-accordion="' + accId + '">' +
          '<span class="pdp-tabs__accordion-left">' +
            '<span class="pdp-tabs__tab-icon">' + iconForHeading(s.title) + '</span>' +
            '<span>' + escapeHTML(s.title) + '</span>' +
          '</span>' +
          '<svg class="pdp-tabs__chevron" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>' +
        '</button>' +
        '<div class="pdp-tabs__accordion-content" id="' + accId + '">' +
          '<div class="pdp-tabs__panel-content">' +
            '<h2>' + escapeHTML(s.title) + '</h2>' +
            s.contentHTML +
          '</div>' +
        '</div>';
      accordion.appendChild(item);
    });
  }

  function escapeHTML(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  // Extract a YouTube video ID from any of the URL shapes Shopify's editor
  // might emit (watch?v=, /embed/, youtu.be/, nocookie variant).
  function extractYouTubeId(src) {
    if (!src) return null;
    var m = src.match(/[?&]v=([^&]+)/) ||
      src.match(/youtube(?:-nocookie)?\.com\/embed\/([^?&/]+)/) ||
      src.match(/youtu\.be\/([^?&/]+)/);
    return m ? m[1] : null;
  }

  // Wrap an iframe in a responsive 16:9 container. For YouTube, add a
  // high-res lite poster (maxresdefault.jpg) + a native-style play button;
  // the real iframe only loads when the user clicks, so the pre-play image
  // is crisp at any panel width and the page isn't loading multiple YT
  // players up front.
  function buildVideoEmbed(iframe) {
    var src = iframe.getAttribute('src') || '';
    var ytId = extractYouTubeId(src);

    iframe.removeAttribute('width');
    iframe.removeAttribute('height');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', '');

    var wrap = document.createElement('div');
    wrap.className = 'pdp-tabs__video-embed';
    iframe.parentNode.insertBefore(wrap, iframe);
    wrap.appendChild(iframe);

    if (ytId) {
      wrap.classList.add('pdp-tabs__video-embed--lite');

      var poster = document.createElement('img');
      poster.className = 'pdp-tabs__video-poster';
      poster.loading = 'lazy';
      poster.alt = iframe.getAttribute('title') || '';

      // Not every YouTube video has a maxresdefault.jpg. When one doesn't
      // exist, YouTube serves a 120×90 gray placeholder with a 200 status
      // (not a 404), so we detect it by natural dimensions and step down
      // through the available sizes until a real thumbnail loads.
      var ytSizes = ['maxresdefault', 'sddefault', 'hqdefault'];
      var ytSizeIdx = 0;
      var stepDown = function () {
        ytSizeIdx++;
        if (ytSizeIdx < ytSizes.length) {
          poster.src = 'https://i.ytimg.com/vi/' + ytId + '/' + ytSizes[ytSizeIdx] + '.jpg';
        }
      };
      poster.addEventListener('load', function () {
        if (poster.naturalWidth === 120 && poster.naturalHeight === 90) stepDown();
      });
      poster.addEventListener('error', stepDown);
      poster.src = 'https://i.ytimg.com/vi/' + ytId + '/' + ytSizes[0] + '.jpg';

      var playBtn = document.createElement('button');
      playBtn.type = 'button';
      playBtn.className = 'pdp-tabs__video-play';
      playBtn.setAttribute('aria-label', 'Play video');
      playBtn.innerHTML =
        '<svg viewBox="0 0 68 48" aria-hidden="true">' +
          '<path class="pdp-tabs__video-play-bg" d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"/>' +
          '<path d="M45 24 27 14v20z" fill="#fff"/>' +
        '</svg>';

      iframe.style.display = 'none';
      wrap.appendChild(poster);
      wrap.appendChild(playBtn);

      var activate = function () {
        var newSrc = src + (src.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
        iframe.setAttribute('src', newSrc);
        iframe.style.display = 'block';
        poster.remove();
        playBtn.remove();
        wrap.classList.remove('pdp-tabs__video-embed--lite');
      };
      playBtn.addEventListener('click', activate);
      poster.addEventListener('click', activate);
    }

    return wrap;
  }

  // After panels/accordion are built, wrap every YouTube/Vimeo iframe in a
  // responsive embed. If a panel has 2+ videos, lift them ALL out of their
  // original wrappers (which may be <p> tags, etc.) and drop them into a
  // single 2-column grid — so every row fills two cells regardless of
  // whether there's text between the videos in the description.
  function enhanceVideoEmbeds(root) {
    var panels = root.querySelectorAll('.pdp-tabs__panel-content');
    Array.prototype.forEach.call(panels, function (panel) {
      var iframes = [];
      Array.prototype.forEach.call(panel.querySelectorAll('iframe'), function (f) {
        var src = f.getAttribute('src') || '';
        if (/youtube\.com|youtu\.be|vimeo\.com/i.test(src)) iframes.push(f);
      });
      if (!iframes.length) return;

      var embeds = iframes.map(buildVideoEmbed);
      if (embeds.length < 2) return;

      // Insert the grid at the top-level ancestor of the first embed so it
      // sits where the first video used to be (preserving relative order
      // versus surrounding text).
      var anchor = embeds[0];
      while (anchor.parentNode && anchor.parentNode !== panel) {
        anchor = anchor.parentNode;
      }
      var grid = document.createElement('div');
      grid.className = 'pdp-tabs__video-grid';
      panel.insertBefore(grid, anchor);

      embeds.forEach(function (embed) {
        var orig = embed.parentNode;
        grid.appendChild(embed);
        // Clean up empty <p>/<div> wrappers left behind.
        while (orig && orig !== panel && orig !== grid) {
          if (orig.children.length === 0 && !(orig.textContent || '').trim()) {
            var p = orig.parentNode;
            orig.remove();
            orig = p;
          } else {
            break;
          }
        }
      });
    });
  }

  function wireTabSwitching(section) {
    var tabBtns = section.querySelectorAll('.pdp-tabs__tab-btn');
    var panels = section.querySelectorAll('.pdp-tabs__panel');
    tabBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var targetId = this.getAttribute('data-tab-target');
        var wasActive = this.classList.contains('is-active');
        tabBtns.forEach(function (b) { b.classList.remove('is-active'); });
        panels.forEach(function (p) { p.classList.remove('is-active'); });
        // Clicking an already-open tab closes it, leaving no panel shown.
        if (!wasActive) {
          this.classList.add('is-active');
          var target = document.getElementById(targetId);
          if (target) target.classList.add('is-active');
        }
      });
    });
  }

  function wireAccordion(section) {
    var triggers = section.querySelectorAll('.pdp-tabs__accordion-trigger');
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var targetId = this.getAttribute('data-accordion');
        var target = document.getElementById(targetId);
        var isActive = this.classList.contains('is-active');
        triggers.forEach(function (t) { t.classList.remove('is-active'); });
        section.querySelectorAll('.pdp-tabs__accordion-content').forEach(function (c) { c.classList.remove('is-open'); });
        if (!isActive) {
          this.classList.add('is-active');
          if (target) target.classList.add('is-open');
        }
      });
    });
  }

  function init() {
    var section = document.querySelector('[data-pdp-tabs]');
    if (!section) return;
    var sourceTpl = document.querySelector('[data-pdp-tabs-source]');
    if (!sourceTpl) return;

    // Read the description HTML from the <template> element.
    var sourceHTML = sourceTpl.innerHTML;
    var sections = parseSections(sourceHTML);

    if (!sections.length) {
      // No usable heading level found — render the raw description as a
      // single block so the product still shows its content.
      var fallbackPanel = document.createElement('div');
      fallbackPanel.className = 'pdp-tabs__panel is-active pdp-tabs__panel--fallback';
      fallbackPanel.innerHTML = '<div class="pdp-tabs__panel-content">' + sourceHTML + '</div>';
      var content = section.querySelector('[data-pdp-tabs-content]');
      if (content) content.appendChild(fallbackPanel);
      enhanceVideoEmbeds(section);
      section.hidden = false;
      return;
    }

    buildDesktopNav(section.querySelector('[data-pdp-tabs-nav]'), sections);
    buildDesktopPanels(section.querySelector('[data-pdp-tabs-content]'), sections);
    buildMobileAccordion(section.querySelector('[data-pdp-tabs-accordion]'), sections);

    enhanceVideoEmbeds(section);

    wireTabSwitching(section);
    wireAccordion(section);

    section.hidden = false;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
